import React, { useState, useEffect } from "react";
import "./css/SessionPlan.css";
import logo from "./KICKS-6.png";
import { fetchUserAttributes } from "@aws-amplify/auth";
import config from "./config";

const SessionPlan = () => {
  const [sessionType, setSessionType] = useState([]);
  const [ageGroup, setAgeGroup] = useState("");
  const [pitchSize, setPitchSize] = useState("");
  const [playerFocus, setPlayerFocus] = useState("");
  const [notes, setNotes] = useState("");
  const [userAttributes, setUserAttributes] = useState({});
  const [userId, setUserId] = useState(null);
  const [sessionTypeError, setSessionTypeError] = useState("");
  const [ageGroupError, setAgeGroupError] = useState("");
  const [pitchSizeError, setPitchSizeError] = useState("");

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const attributes = await fetchUserAttributes();
        setUserAttributes(attributes);
        setUserId(attributes.sub); // Assuming `sub` contains the userId
      } catch (error) {
        console.error("Error fetching user attributes:", error);
      }
    };
    fetchAttributes();
  }, []);

  const handleSubmit = async () => {
    let isValid = true;

    if (sessionType.length === 0) {
      setSessionTypeError("Please select at least one session type.");
      isValid = false;
    } else {
      setSessionTypeError("");
    }

    if (!ageGroup) {
      setAgeGroupError("Please select an age group.");
      isValid = false;
    } else {
      setAgeGroupError("");
    }

    if (!pitchSize) {
      setPitchSizeError("Please select a pitch size.");
      isValid = false;
    } else {
      setPitchSizeError("");
    }

    if (!isValid) {
      return;
    }

    if (!userId) {
      alert("User ID is not defined. Please refresh the page and try again.");
      return;
    }

    const currentDate = new Date();
    const date = currentDate.toISOString().split("T")[0];
    const time = currentDate
      .toISOString()
      .split("T")[1]
      .replace(/:/g, "-")
      .split(".")[0];
    const folderPath = `${userId}/${date}/${time}`;

    const sessionInfo = {
      sessionType,
      ageGroup,
      pitchSize,
      playerFocus: playerFocus || "None specified",
      notes: notes || "No additional notes provided.",
      userAttributes,
      userId, // Include userId in the payload
    };

    console.log("Session Info Payload:", sessionInfo);

    // Call the API Gateway URL to trigger Lambda
    await invokeLambda(sessionInfo);
  };

  // Function to invoke the Lambda function via API Gateway
  const invokeLambda = async (sessionInfo) => {
    const apiUrl =
      "https://jpl36qoz9f.execute-api.us-east-2.amazonaws.com/default/create_ad_hoc_session_to_email";

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          body: JSON.stringify({
            // Wrap sessionInfo in another JSON.stringify
            email: userAttributes.email, // Add the email directly from userAttributes
            sessionType: sessionInfo.sessionType,
            ageGroup: sessionInfo.ageGroup,
            pitchSize: sessionInfo.pitchSize,
            playerFocus: sessionInfo.playerFocus,
            notes: sessionInfo.notes,
          }),
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log("Lambda response:", responseData);
        alert("Session plan submitted successfully!");
      } else {
        console.error("Error invoking Lambda:", response.statusText);
        alert("Error submitting session plan. Please try again.");
      }
    } catch (error) {
      console.error("Error invoking Lambda:", error);
      alert("An error occurred while submitting the session plan.");
    }
  };

  return (
    <div className="session-plan-container">
      <img src={logo} alt="KICKS Logo" className="logo-session" />

      <h1 className="session-plan-title">Plan a Session</h1>
      <p className="session-plan-description">
        Plan an ad-hoc session and get the session details sent straight to your
        email.
      </p>

      <div className="session-type-section">
        <h3>Select Session Type:</h3>
        <div className="session-type-buttons">
          {[
            "In Possession",
            "Out Possession",
            "Attacking",
            "Defending",
            "Possession",
            "Pressing",
            "Agility",
            "Change of Speed",
            "Finishing",
            "Creating Chances",
          ].map((type) => (
            <button
              key={type}
              className={`session-type-button ${
                sessionType.includes(type) ? "active" : ""
              }`}
              onClick={() =>
                setSessionType((prev) =>
                  prev.includes(type)
                    ? prev.filter((t) => t !== type)
                    : [...prev, type]
                )
              }
            >
              {type}
            </button>
          ))}
        </div>
        {sessionTypeError && (
          <p className="error-message">{sessionTypeError}</p>
        )}
      </div>

      <div className="age-group-section">
        <h3>Select Age Group:</h3>
        <select
          className="age-group-select"
          onChange={(e) => setAgeGroup(e.target.value)}
          value={ageGroup}
        >
          <option value="" disabled>
            Select Age Group
          </option>
          {["U6-U8", "U9-U12", "U13-U14", "U15-U17", "U18+", "Veterans"].map(
            (group) => (
              <option key={group} value={group}>
                {group}
              </option>
            )
          )}
        </select>
        {ageGroupError && <p className="error-message">{ageGroupError}</p>}
      </div>

      <div className="pitch-section">
        <h3>Select Pitch Size:</h3>
        <select
          className="pitch-select"
          onChange={(e) => setPitchSize(e.target.value)}
          value={pitchSize}
        >
          <option value="" disabled>
            Pitch Size
          </option>
          {["FULL PITCH", "HALF PITCH", "QUARTER PITCH"].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        {pitchSizeError && <p className="error-message">{pitchSizeError}</p>}
      </div>

      <div className="player-focus-section">
        <h3>Player Focus (Optional):</h3>
        <textarea
          className="player-focus-textarea"
          placeholder="Enter player names or leave blank"
          value={playerFocus}
          onChange={(e) => setPlayerFocus(e.target.value)}
        />
      </div>

      <div className="notes-section">
        <h3>Additional Notes (Optional):</h3>
        <textarea
          className="notes-textarea"
          placeholder="Any additional instructions or notes for the session"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <button className="submit-button" onClick={handleSubmit}>
        Plan Session
      </button>
    </div>
  );
};

export default SessionPlan;
